import React, { use, useCallback, useEffect, useMemo } from 'react'
import { type Location, useLocation, useNavigate } from 'react-router'
import { useApolloClient } from '@apollo/client'
import { toSeconds } from 'duration-fns'
import { useLDClient } from 'launchdarkly-react-client-sdk'
import { useLocalStorage } from 'usehooks-ts'

import { FormattedMessage } from '@divvy-web/i18n'
import { TOAST_TYPE_BASIC, useToast } from '@divvy-web/skylab.toast'

import { useAuthIdleness } from '@sputnik/auth/useAuthIdleness'
import { useThreatMetrixProfiling } from '@sputnik/hooks/useThreatMetrixProfiling'
import { LOCAL_STORAGE_KEYS, PATHNAME_CONSTS } from '@sputnik/resources/constants'
import { logError, logInfo } from '@sputnik/utils/loggerUtils'

import { useRefreshToken } from './gql/RefreshToken.gql'
import { useRequestApplicantToken } from './gql/RequestApplicantToken.gql'
import { RequestAuthCode, useRequestAuthCode } from './gql/RequestAuthCode.gql'
import { useVerifyAuthCode } from './gql/VerifyAuthCode.gql'
import {
  getCreditAppIdFromToken,
  getEmailHashFromToken,
  getExpirationFromToken,
  getJwtLookupIdFromToken,
  getSessionIdFromToken,
} from './jwt'
import { clearAllLocalStorageValues } from './localStorageUtils'
import { isTokenExpired } from './tokenUtils'
import { useAuthTimeoutCountdown } from './useAuthTimeoutCountdown'

type ResetAuthReason =
  'LogoutButtonClicked' | 'ExpiredToken' | 'NotAuthenticated' | 'SignerDifferentFromCurrentUser' | 'CountdownEnded'

interface ResetAuthParams {
  source?: string
  location?: Location
  reason?: ResetAuthReason
}

interface AuthContextValue {
  getCreditAppIdOnToken: () => string | null | undefined
  getJwtLookupIdOnToken: () => string | null | undefined
  email: string | null
  setEmail: (newEmail: string) => void
  isAuthExpired: () => boolean
  isAuthenticated: boolean
  logout: (params?: ResetAuthParams) => void
  refreshAuthToken: () => Promise<void>
  requestAuthCode: (requestedEmail: string) => Promise<RequestAuthCode | null | undefined>
  requestApplicantToken: (salesforceCreditId: string, skipEmails?: boolean) => Promise<void>
  resetAuth: (params?: ResetAuthParams) => void
  setToken: (newToken: string) => void
  verifyAuthCode: (authCode: string, emailArg: string | null) => Promise<void>
  isUserIdle: boolean
  authenticatedSecondsRemaining: number
  setAuthState: (emailArg: string, token: string) => void
}

const AuthContext = React.createContext<AuthContextValue>({
  getCreditAppIdOnToken: () => '',
  getJwtLookupIdOnToken: () => '',
  email: '',
  setEmail: () => {},
  isAuthExpired: () => false,
  isAuthenticated: false,
  logout: () => {},
  refreshAuthToken: async () => {
    return
  },
  requestAuthCode: async () => {
    return null
  },
  requestApplicantToken: async () => {
    return
  },
  resetAuth: () => {},
  setToken: () => {},
  verifyAuthCode: async () => {
    return
  },
  isUserIdle: false,
  authenticatedSecondsRemaining: 15 * 60,
  setAuthState: () => {},
})

export const AuthProvider = ({ children }: React.PropsWithChildren) => {
  const apolloClient = useApolloClient()
  const showToast = useToast()
  const navigate = useNavigate()
  const { search } = useLocation()
  const launchDarklyClient = useLDClient()

  const [token, setToken] = useLocalStorage<string | null>(LOCAL_STORAGE_KEYS.AUTH_STORAGE_TOKEN_KEY, null)
  const [authId, setAuthId] = useLocalStorage<string | null>(LOCAL_STORAGE_KEYS.APPLICANT_AUTH_ID, null)
  const [_emailHash, setEmailHash] = useLocalStorage<string | null>(LOCAL_STORAGE_KEYS.APPLICANT_EMAIL_HASH_KEY, null)
  const [email, setEmail] = useLocalStorage<string | null>(LOCAL_STORAGE_KEYS.APPLICANT_EMAIL_KEY, null)

  const [authenticatedSecondsRemaining, { stopCountdown, startCountdown, resetCountdown }] = useAuthTimeoutCountdown()
  const { isIdle } = useAuthIdleness()

  const isAuthenticated = useMemo(() => !!token, [token])

  const [requestAuthenticationCode] = useRequestAuthCode()
  const [verifyCode] = useVerifyAuthCode()
  const [requestToken] = useRequestApplicantToken()
  const [refreshToken] = useRefreshToken()

  useThreatMetrixProfiling(authId)

  const updateToken = useCallback(
    (jwt: string | undefined) => {
      setToken(jwt || null)

      const tokenExpirationTime = getExpirationFromToken(jwt || null)

      resetCountdown(Math.ceil(toSeconds((tokenExpirationTime ? tokenExpirationTime * 1000 : Date.now()) - Date.now())))
      startCountdown()
    },
    [resetCountdown, setToken, startCountdown],
  )

  const isAuthExpired = useCallback(() => {
    return isTokenExpired(token)
  }, [token])

  const getCreditAppIdOnToken = useCallback(() => {
    return getCreditAppIdFromToken(token)
  }, [token])

  const getJwtLookupIdOnToken = useCallback(() => {
    return getJwtLookupIdFromToken(token)
  }, [token])

  const requestAuthCode = useCallback(
    async (requestedEmail: string) => {
      logInfo('requestAuthCode', {
        attributes: {
          result: 'Start requesting authentication code',
        },
        eventName: 'SignIn',
      })

      setEmail(requestedEmail.toLowerCase())
      const { data } = await requestAuthenticationCode({
        variables: { email: requestedEmail },
        onError: (e) => {
          logError(e, {
            attributes: {
              action: 'requestAuthCode',
              message: e?.message,
              result: 'Error requesting auth code',
            },
            eventName: 'SignIn',
          })
          throw e
        },
      })

      return data
    },
    [requestAuthenticationCode, setEmail],
  )

  const refreshAuthToken = useCallback(async () => {
    stopCountdown()
    logInfo('refreshAuthToken', {
      attributes: {
        result: 'Start refreshing auth token',
      },
      eventName: 'Timeout',
    })

    const { data } = await refreshToken({
      onError: (e) => {
        logError(e, {
          attributes: {
            action: 'refreshAuthToken',
            message: e?.message,
            result: 'Error refreshing auth token',
          },
          eventName: 'Timeout',
        })
        throw e
      },
    })
    const { token } = data?.refreshToken || {}
    updateToken(token)

    logInfo('refreshAuthToken', {
      attributes: {
        result: 'Auth token refreshed',
      },
      eventName: 'Timeout',
    })
  }, [refreshToken, stopCountdown, updateToken])

  const processPreSignIn = useCallback(
    (emailArg: string | null) => {
      if (emailArg) {
        setEmail(emailArg.toLowerCase())
      }
    },
    [setEmail],
  )

  const processPostSignIn = useCallback(
    async (emailArg: string, token: string) => {
      showToast(
        TOAST_TYPE_BASIC,
        <FormattedMessage
          defaultMessage='You are signed in as {email}'
          id='sputnik.auth__RMGhLe'
          values={{ email: emailArg }}
        />,
        {
          closeMsg: (
            <FormattedMessage
              defaultMessage='Dismiss'
              id='sputnik.Dashboard__TDaF6J'
            />
          ),
          dataTestId: 'signedin-email-toast',
        },
      )

      updateToken(token)
      const tokenEmailHash = getEmailHashFromToken(token ?? null) ?? ''
      setEmailHash(tokenEmailHash)

      setAuthId(getSessionIdFromToken(token || null) ?? null)

      if (window.__DivvyEnvironment.LAUNCH_DARKLY_CLIENT_SIDE_ID && !window.__DivvyEnvironment.LAUNCH_DARKLY_DISABLED) {
        try {
          await launchDarklyClient?.identify({
            kind: 'multi',
            creditApplicant: {
              key: tokenEmailHash,
              email,
              _meta: {
                privateAttributes: ['/email'],
              },
            },
          })
          logInfo('identifyWithLaunchDarkly', {
            attributes: {
              result: 'Susccessfully identified with LD',
            },
            eventName: 'IdentifyWithLDSuccess',
          })
        } catch (err) {
          logError(err, {
            attributes: {
              action: 'identifyWithLaunchDarkly',
              result: 'Failed to identify with LD',
              message: err instanceof Error ? err?.message : err,
            },
            eventName: 'IdentifyWithLDFailure',
          })
        }
      }

      navigate({ pathname: `/${PATHNAME_CONSTS.POST_LOGIN_PATH}`, search }, { replace: true })
    },
    [email, launchDarklyClient, navigate, search, setAuthId, setEmailHash, showToast, updateToken],
  )

  const setAuthState = useCallback(
    (emailArg: string, token: string) => {
      processPreSignIn(emailArg)

      processPostSignIn(emailArg, token)
    },
    [processPostSignIn, processPreSignIn],
  )

  const verifyAuthCode = useCallback(
    async (authCode: string, emailArg: string | null = null) => {
      logInfo('verifyAuthCode', {
        attributes: {
          result: 'Start verifying access code',
        },
        eventName: 'SignIn',
      })

      processPreSignIn(emailArg ?? email ?? '')

      const { data: tokenData } = await verifyCode({
        fetchPolicy: 'no-cache',
        variables: { authCode, email: emailArg || email || '' },
        onError: (e) => {
          logError(e, {
            attributes: {
              action: 'verifyAuthCode',
              message: e?.message,
              result: 'Error verifying access code',
            },
            eventName: 'SignIn',
          })
          throw e
        },
      })

      logInfo('verifyAuthCode', {
        attributes: {
          result: 'Auth code verified',
        },
        eventName: 'SignIn',
      })

      const { token } = tokenData?.verifyAuthCode ?? {}

      processPostSignIn(emailArg || email || '', token ?? '')
    },
    [email, processPostSignIn, processPreSignIn, verifyCode],
  )

  /**
   * Requests an applicant token from the server.
   *
   * @param {string} salesforceCreditId - The Salesforce Credit ID of the credit application.
   * @param {boolean} [skipEmails=false] - Whether to skip scheduling application completion reminder emails.
   * @returns {Promise<string>} A promise that resolves with the applicant token.
   */
  const requestApplicantToken = useCallback(
    async (salesforceCreditId: string, skipEmails: boolean = false) => {
      const { data } = await requestToken({ variables: { salesforceCreditId, skipEmails } })

      const { token } = data?.requestApplicantToken ?? {}
      updateToken(token)
    },
    [requestToken, updateToken],
  )

  const resetAuth = useCallback(
    (params?: ResetAuthParams) => {
      stopCountdown()
      clearAllLocalStorageValues()
      apolloClient.clearStore()
      apolloClient.cache.reset()

      logInfo('resetAuth', {
        attributes: {
          source: params?.source ?? 'AuthProvider',
          location: params?.location ?? 'N/A',
          reason: params?.reason ?? 'N/A',
        },
        eventName: 'AuthReset',
        message: 'auth has been reset',
      })
    },
    [apolloClient, stopCountdown],
  )

  const logout = useCallback(
    (params?: ResetAuthParams) => {
      logInfo('logout', {
        attributes: {
          result: 'Log out and send user to /auth',
        },
        eventName: 'CreditAppLogOut',
        message: 'the user has logged out',
      })
      resetAuth(params)
      navigate(
        {
          pathname: PATHNAME_CONSTS.AUTH_PATH,
          search,
        },
        { state: { logout: 'self' } },
      )
    },
    [navigate, resetAuth, search],
  )

  useEffect(() => {
    if (isAuthenticated && authenticatedSecondsRemaining > 0 && authenticatedSecondsRemaining <= 1 * 60 && !isIdle) {
      refreshAuthToken()
    }
  }, [isAuthenticated, isIdle, refreshAuthToken, authenticatedSecondsRemaining])

  const context = {
    email,
    isAuthExpired,
    isAuthenticated,
    getCreditAppIdOnToken,
    getJwtLookupIdOnToken,
    logout,
    refreshAuthToken,
    requestApplicantToken,
    requestAuthCode,
    resetAuth,
    setEmail,
    setToken,
    verifyAuthCode,
    isUserIdle: isIdle,
    authenticatedSecondsRemaining,
    setAuthState,
  }
  return <AuthContext value={context}>{children}</AuthContext>
}

export const useAuth = () => {
  const context = use(AuthContext)

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }

  return context
}
